import {
  countryName,
  ConfirmDialog,
  addBooking,
  adjustVariant,
  addStop,
  deleteStop,
  getDocs,
  getFamily,
  getPlan,
  getVariants,
  Forbidden,
  NotFound,
  heldBy,
  partedBy,
  PencilIcon,
  TicketPlusIcon,
  reorderStops,
  setEnds,
  setTransfer,
  setPassport,
  undoVariant,
  updateStop,
  type Booking,
  type Spot,
  type DocsScreen,
  type Family,
  type Plan,
  type TripWeather,
  type PlanStop,
  type Mode,
  type RouteSearch,
  type Variants,
} from '@trippath/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { apart, kinMap } from '../../lib/map';
import { MapPick, stopStart } from './MapPick';
import { BudgetCard, DocsCard, StatsCard, VisaCard } from './PlanSide';
import { PlanMap } from './PlanMap';
import { createMoment } from '../../lib/moment';
import { PlanSearch } from '../../components/search/PlanSearch';
import {
  StopDetail,
  ticketWayOf,
  ticketWord,
  type TicketWay,
} from '../../components/stop/StopDetail';
import { IncidentDialog } from '../../components/booking/Incident';
import {
  BranchDialog,
  FamilyCard,
  FamilyRoute,
  KinStopDetail,
  kinOf,
  kinWay,
  stopNotes,
  kinTo,
} from '../../components/family/Family';
import { StopDialog, type StopStart } from '../../components/stop/StopDialog';
import { ChangeBooking, EditStop } from '../../components/stop/StopEdits';
import type { Sector } from '../../components/booking/TicketEditor';
import { sectorOf, ticketOf } from '../../lib/wayTicket';
import { Timeline } from '../../components/timeline/Timeline';
import type { MapPlace } from '../../components/map/TripMap';
import { Travellers } from '../../components/travellers/Travellers';
import { Proposal } from './Proposal';
import { togglePick } from './picks';
import { NO_NOTES, placeOf, RouteList } from './RouteList';
import { BookingDialog } from '../../components/booking/BookingDialog';
import { CarDialog } from './CarDialog';
import { stopInputOf, withDays, withWayAt } from '../../lib/stopInput';
import { PickedPath } from '../../components/stop/PickedPath';
import { WayDetail } from '../../components/way/WayDetail';
import { draftsOf, saidWith } from '../../lib/variantEdit';
import { Variants as VariantsWidget } from '../../components/variants/Variants';
import { DockFrame } from '../../components/widget/Dock';
import { useShown, Widget, WidgetDock } from '../../components/widget/Widget';
import { BranchIcon } from '../../components/icons/BranchIcon';
import { readyWeather, useWeather, weatherOf } from '../../components/weather/useWeather';
import { useLive } from '../../app/live';
import { useReadOnly } from '../../app/readOnly';
import type { PlaceChange } from '../../lib/variantEdit';

/** Where the widgets start over the map; each person moves them where they like. */
const LEFT = { left: 16, top: 96 };
const RIGHT = { right: 16, top: 16 };
const TOP = { left: 352, top: 16 };
const BOTTOM = { left: 352, bottom: 64, top: 16 };
/** Below the search. */
const BOTTOM_LEFT = { left: 16, bottom: 64, top: 96 };
/** On the right, from the bottom: under the budget, as high as there is room. */
const BOTTOM_RIGHT = { right: 16, bottom: 64, top: 16 };
/** What shows on a first visit; the dock opens and closes each. */
const START = {
  route: true,
  budget: true,
  timeline: true,
  stop: false,
  more: false,
  variants: false,
  visa: false,
};
/** The room between the route on the left and the budget on the right. */
const between = (area: number) => Math.max(320, area - 352 - 332);

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

type Editing =
  | { kind: 'none' }
  /** A stop to add: at the end, or after one, into the days no stop covers. */
  | { kind: 'new-stop'; start?: StopStart; after?: PlanStop }
  | { kind: 'stop'; stop: PlanStop };

/** What is shown beside the plan and fails stays as it was: the plan stands without it. */
const quiet = () => undefined;

/** One trip: its route, what it costs against the budget, and its countries (docs/01 §2). */
export function Planner({ opens = [] }: { opens?: readonly string[] }) {
  const { t, i18n } = useTranslation();
  // A trip read only: looked at, nothing of it changed.
  const locked = useReadOnly();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  // The trip's weather, a row on its timeline.
  const sky = useWeather(id);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [editing, setEditing] = useState<Editing>({ kind: 'none' });
  const [failed, setFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Every stop let go, by a press on nothing on the timeline.
  const [loose, setLoose] = useState(false);
  // A day's weather pressed on the timeline: that stop's day shown in full.
  const [skyDay, setSkyDay] = useState<{ stop: string; day: string } | null>(null);
  const skyOf = (stop: string) => (skyDay?.stop === stop ? skyDay.day : null);
  // How many times a stop has been picked: each pick flies the map to it, the same one too.
  const [picks, setPicks] = useState(0);
  // The stop whose way on was pressed on the map: its widget open, the way standing out.
  const [way, setWay] = useState<string | null>(null);
  // The stop asked about before it is removed.
  const [removing, setRemoving] = useState<PlanStop | null>(null);
  // The stop whose way on a ticket is being booked for, and whether the car's fuel is open.
  const [ticketFor, setTicketFor] = useState<{ stop: PlanStop; way: TicketWay } | null>(null);
  const [fuelOpen, setFuelOpen] = useState(false);
  // The stop a stay is being booked at, in place.
  const [bookingFor, setBookingFor] = useState<PlanStop | null>(null);
  // A booking something happened to, with the stop it is kept on.
  const [incidentFor, setIncidentFor] = useState<{ booking: Booking; stop: PlanStop } | null>(
    null,
  );
  // A booking opened from its stop, to change or remove.
  const [changing, setChanging] = useState<{
    booking: Booking;
    stop: PlanStop;
    sector: Sector | null;
  } | null>(null);
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [draft, setDraft] = useState<RouteSearch | null>(null);
  const [draftModes, setDraftModes] = useState<Mode[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  // A variant just used can be taken back, until anything else changes.
  const [undoable, setUndoable] = useState(false);
  // The middle of the map as the person left it, and a place name clicked on it.
  const [center, setCenter] = useState<{ lat: number; lon: number } | null>(null);
  // The moment the map shows the sky at, within the trip: its first day's start to its last's end.
  const [moment] = useState(() => createMoment());
  const tripStart = state.kind === 'ready' ? state.plan.trip.start : null;
  const tripEnd = state.kind === 'ready' ? state.plan.trip.end : null;
  useEffect(() => {
    if (tripStart === null || tripEnd === null) return;
    moment.within(
      Date.parse(`${tripStart}T00:00:00Z`),
      Date.parse(`${tripEnd}T00:00:00Z`) + 86_400_000,
    );
  }, [moment, tripStart, tripEnd]);
  const [picked, setPicked] = useState<MapPlace | null>(null);
  // Towns picked on the map for a way there: each by its name and the point it was picked at.
  const [pins, setPins] = useState<string[]>([]);
  // The trip's variants, and the one shown on the map.
  const [variants, setVariants] = useState<Variants | null>(null);
  // The variants shown, remembered for the trip in this browser.
  const [variantIds, setShownVariants] = useState<string[]>(() => {
    try {
      const kept: unknown = JSON.parse(localStorage.getItem(`trippath.variants.${id}`) ?? '[]');
      return Array.isArray(kept) ? kept.filter((v) => typeof v === 'string') : [];
    } catch {
      return [];
    }
  });
  const setVariantIds = (next: string[]) => {
    setShownVariants(next);
    // A place picked goes with its variant put away.
    if (selectedId?.startsWith('p-') && !next.some((v) => selectedId.startsWith(`p-${v}-`))) {
      setSelectedId(null);
    }
    try {
      localStorage.setItem(`trippath.variants.${id}`, JSON.stringify(next));
    } catch {
      // Nothing kept: they are shown until the page is left.
    }
  };
  // Those shown that are still kept, in the order they were picked: the same list until either
  // changes, so the map does not draw them again on every render.
  const shownVariants = useMemo(
    () =>
      variantIds.flatMap((shown) => (variants?.variants ?? []).filter((v) => v.id === shown)),
    [variants, variantIds],
  );
  const setQuery = useCallback(
    (next: string) => {
      setParams(next === '' ? {} : { q: next }, { replace: true });
    },
    [setParams],
  );

  // The note that a route was added goes after a few seconds.
  useEffect(() => {
    if (toast === null) return;
    const gone = setTimeout(() => {
      setToast(null);
    }, 4000);
    return () => {
      clearTimeout(gone);
    };
  }, [toast]);
  const [docs, setDocs] = useState<DocsScreen | null>(null);
  // The trip's family, and the stop a branch is being made at.
  const [family, setFamily] = useState<Family | null>(null);
  const [branching, setBranching] = useState<{ at: string | null } | null>(null);
  // A stop of another trip of the family picked on the rail or the timeline: its way shown on the
  // map, the map flown to it; this trip stays as it is.
  // Its way on picked, as this trip's own ways are: the way stands out on the map, not the stop.
  const [kinPicked0, setKinPick] = useState<{
    in: string;
    trip: string;
    stop: string;
    way: boolean;
  } | null>(null);
  // Picked on this trip's page only: another trip opened starts with nothing of it picked.
  const kinPick = kinPicked0?.in === id ? kinPicked0 : null;
  const kinTrip = family?.trips.find((trip) => trip.id === kinPick?.trip) ?? null;
  const kinData = useMemo(
    () =>
      family?.trips
        .filter((trip) => trip.id === kinPick?.trip)
        .map((trip) => kinMap(kinWay(family, trip)))[0] ?? null,
    [family, kinPick],
  );
  // Where the trip's branches part, said after those stops' days: made again only with the family.
  const notes = useMemo(
    () =>
      family
        ? stopNotes(family, id, {
            parts: (count) => t('family.partsHere', { count }),
            joins: (name, day) => t('family.joinsHere', { name, day }),
          })
        : NO_NOTES,
    [family, id, t],
  );
  const [canvas, setCanvas] = useState<HTMLDivElement | null>(null);
  const [shown, show] = useShown(START, opens);
  // The family widget shows at first once the trip has branches; then as the person leaves it.
  const families = shown.family ?? (family !== null && family.trips.length > 1);
  const docked = useMemo(() => ({ ...shown, family: families }), [shown, families]);

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        // A viewer may not see the plan with its money: their page is the route and dates.
        if (error instanceof Forbidden) void navigate(`/trips/${id}/view`, { replace: true });
        else setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id, navigate, changed]);

  // The variants as the plan now is: read again each time it changes.
  const ready = state.kind === 'ready' ? state.plan : null;
  useEffect(() => {
    if (ready === null) return;
    getVariants(ready.trip.id).then(setVariants, () => undefined);
  }, [ready]);

  // The trip's family, as the plan now is; the plan stands without it.
  useEffect(() => {
    if (ready === null) return;
    getFamily(ready.trip.id).then(setFamily, quiet);
  }, [ready]);

  // The visa days and the documents in short, as the plan now is; the plan stands without them.
  useEffect(() => {
    if (ready === null) return;
    getDocs(ready.trip.id).then(setDocs, quiet);
  }, [ready]);

  // A stop asked for by its address (`?stop=`, from the bar's search or another trip's widget):
  // picked, its widget open, the map flown to it; the address keeps no more of it.
  const asked = params.get('stop');
  const [taken, setTaken] = useState<string | null>(null);
  if (asked !== null && asked !== taken) {
    setTaken(asked);
    setKinPick(null);
    setSelectedId(asked);
    setPicks((was) => was + 1);
    setWay(null);
  }
  // Taken off the address, the same stop asked for again is picked again.
  if (asked === null && taken !== null) setTaken(null);
  useEffect(() => {
    if (asked === null) return;
    show('stop', true);
    setParams(
      (was) => {
        const next = new URLSearchParams(was);
        next.delete('stop');
        return next;
      },
      { replace: true },
    );
  }, [asked, setParams, show]);

  // Another trip's stop picked, on a trip the person is on: that trip's plan, so the stop is
  // shown as one of this trip's is.
  const kinId = kinTrip !== null && kinTrip.role !== null ? kinTrip.id : null;
  const [kinPlan, setKinPlan] = useState<Plan | null>(null);
  useEffect(() => {
    if (kinId === null) return;
    getPlan(kinId).then(setKinPlan, quiet);
  }, [kinId]);
  // Its weather too, on the timeline under its row.
  const [kinSky, setKinSky] = useState<{ trip: string; weather: TripWeather } | null>(null);
  useEffect(() => {
    if (kinId === null) return;
    weatherOf(kinId).then((weather) => {
      setKinSky({ trip: kinId, weather });
    }, quiet);
  }, [kinId]);

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const { plan } = state;
  const close = () => {
    setEditing({ kind: 'none' });
  };
  const saved = (next: Plan) => {
    setState({ kind: 'ready', plan: next });
    setEditing({ kind: 'none' });
    setUndoable(false);
  };
  const used = (next: Plan) => {
    saved(next);
    setUndoable(true);
  };
  // The stops after one, the next first: where a ticket from it may go.
  const onFrom = (stop: PlanStop) =>
    plan.stops.slice(plan.stops.indexOf(stop) + 1).map((s) => ({ id: s.id, city: s.city }));
  // The stops a ticket from `stop` to `until` goes on through.
  const through = (stop: PlanStop, until: string | null | undefined) => {
    const after = plan.stops.slice(plan.stops.indexOf(stop) + 1);
    return after.slice(
      0,
      Math.max(
        0,
        after.findIndex((s) => s.id === until),
      ),
    );
  };
  const move = (index: number, by: number) => {
    const ids = plan.stops.map((stop) => stop.id);
    const moving = ids.slice(index, index + 1);
    const order = ids.toSpliced(index, 1).toSpliced(index + by, 0, ...moving);
    // A booked way keeps its two stops together: said, not sent.
    const parted = partedBy(plan, order);
    const to = plan.stops[plan.stops.findIndex((stop) => stop.id === parted?.id) + 1];
    if (parted !== null && to !== undefined) {
      setToast(t('plan.held', { from: parted.city, city: to.city }));
      return;
    }
    setFailed(false);
    reorderStops(plan.trip.id, order).then(saved, () => {
      setFailed(true);
    });
  };
  // A city clicked goes on the end of the route being drafted, or after the plan's last stop
  // that is somewhere.
  const typed = query.trim().replace(/-+$/, '');
  const last = plan.stops.findLast((stop) => stop.place !== null && !stop.unresolved);
  const toRoute = (name: string) => {
    const from = typed === '' ? (last?.city ?? '') : typed;
    setQuery(from === '' ? name : `${from}-${name}`);
    setPicked(null);
  };
  // Where a way to a place clicked would leave from, said on its card.
  const wayFrom = typed === '' ? (last?.city ?? null) : typed.slice(typed.lastIndexOf('-') + 1);
  // The stops already at a place clicked: by its name, or near where it was clicked.
  const visits = (at: MapPlace) =>
    plan.stops.flatMap((stop, index) =>
      stop.city.toLowerCase() === at.name.toLowerCase() ||
      (stop.place !== null && apart(stop.place, at) < 0.2)
        ? [{ id: stop.id, number: index + 1, nights: stop.days }]
        : [],
    );
  const asStop = (at: MapPlace) => {
    setPicked(null);
    void stopStart(at, plan.trip.currency).then((start) => {
      setEditing({ kind: 'new-stop', start });
    });
  };
  const kept = variants?.variants ?? [];
  const pickVariant = (variant: string) => {
    setVariantIds(togglePick(plan.stops, kept, variantIds, variant));
  };
  // A variant's new place picked, or else the stop picked: the first until one is.
  const place = placeOf(kept, selectedId);
  // Nothing picked by hand, the first stop is shown; let go on the timeline, none is.
  const selected =
    place || loose
      ? undefined
      : (plan.stops.find((stop) => stop.id === selectedId) ?? plan.stops[0]);
  // Only what is picked by hand is flown to; the plan opens on the whole route.
  const spot = place ? place.stop.place : selected?.place;
  // A place searched before the route is whole is marked too: the place it goes on from.
  const searched =
    query !== '' && draft?.legs.length === 0
      ? draft.parts.findLast((part) => part.place)?.place
      : undefined;
  let focus = selectedId !== null && spot ? { lat: spot.lat, lon: spot.lon, n: picks } : null;
  if (searched) focus = { lat: searched.lat, lon: searched.lon, n: picks };
  // A way picked is shown by its ends, not the stop picked before it.
  if ((way !== null || kinPick?.way) && !searched) focus = null;
  // Another trip's stop picked: the map goes to it, its widget says it.
  const kinPicked = kinTrip?.stops.find((stop) => stop.id === kinPick?.stop) ?? null;
  const kinStop = kinPicked?.place;
  if (kinStop && !kinPick?.way) focus = { lat: kinStop.lat, lon: kinStop.lon, n: picks };
  const wayStop = plan.stops.find((s) => s.id === way);
  // A variant's place picked shows its variant too.
  // One thing picked at a time: a stop picked, no way is.
  const pickKin = (trip: string, stop: string, way = false) => {
    setKinPick({ in: id, trip, stop, way });
    setPicks((was) => was + 1);
    setWay(null);
    show('stop', true);
  };
  const choose = (key: string) => {
    setLoose(false);
    setKinPick(null);
    setSelectedId(key);
    setPicks((was) => was + 1);
    setWay(null);
    const of = placeOf(kept, key);
    if (of && !variantIds.includes(of.variant.id)) pickVariant(of.variant.id);
  };
  const pickStop = (stop: PlanStop) => {
    choose(stop.id);
  };
  // A way picked, on the map or the timeline, is shown where what is picked is.
  const pickWay = (stop: PlanStop) => {
    setKinPick(null);
    setWay(stop.id);
    show('stop', true);
  };
  // A way changed: its ticket at this leg, else the way itself.
  const wayActions = (stop: PlanStop) => {
    const ticket = ticketWayOf(plan, stop);
    return (
      <>
        {/* A ticket for the way, bought here as from its row in the stop's widget. */}
        {ticket && (
          <button
            type="button"
            className="icon-button icon-button-small"
            aria-label={t(ticketWord(ticket))}
            title={t(ticketWord(ticket))}
            onClick={() => {
              setTicketFor({ stop, way: ticket });
            }}
          >
            <TicketPlusIcon />
          </button>
        )}
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('calendar.editWay')}
          title={t('calendar.editWay')}
          onClick={() => {
            const { holder, ticket } = ticketOf(plan, stop);
            if (ticket) {
              setChanging({ stop: holder, booking: ticket, sector: sectorOf(stop) });
            } else {
              setEditing({ kind: 'stop', stop });
            }
          }}
        >
          <PencilIcon />
        </button>
      </>
    );
  };

  return (
    <DockFrame canvas={setCanvas}>
      <h1 className="visually-hidden">{plan.trip.name}</h1>
      <PlanMap
        plan={plan}
        covered={window.innerWidth > 640}
        draft={query === '' ? null : draft}
        draftModes={draftModes}
        variants={shownVariants}
        focus={focus}
        moment={moment}
        onView={setCenter}
        onPlace={setPicked}
        // The box of a place pressed goes with it as the map moves.
        follow={
          picked && {
            lat: picked.lat,
            lon: picked.lon,
            onMove: (at) => {
              setPicked((was) => was && { ...was, ...at });
            },
          }
        }
        onStay={(id) => {
          // A stay pressed on the map: its booking opened, as from its stop.
          for (const stop of plan.stops) {
            for (const booking of stop.bookings) {
              if (booking.id === id) setChanging({ booking, stop, sector: null });
            }
          }
        }}
        onLine={(id) => {
          // A way of another trip pressed picks its stop there, as its row on the rail does.
          const owner = family?.trips.find(
            (trip) => trip.id !== plan.trip.id && trip.stops.some((stop) => stop.id === id),
          );
          if (owner) {
            pickKin(owner.id, id, true);
            return;
          }
          setKinPick(null);
          setWay(id);
          show('stop', true);
        }}
        way={wayStop ? wayStop.id : kinPick?.way ? kinPick.stop : null}
        kin={kinData}
      />
      <PlanSearch
        plan={plan}
        query={query}
        onQuery={setQuery}
        onRoute={setDraft}
        onWays={setDraftModes}
        near={center}
        pins={pins}
        onAdded={(next, cities) => {
          setState({ kind: 'ready', plan: next });
          setQuery('');
          setToast(t('plan.added', { cities: cities.join(', ') }));
        }}
        onKept={(next, name) => {
          setVariants(next);
          setQuery('');
          show('variants', true);
          setVariantIds(
            togglePick(
              plan.stops,
              next.variants,
              variantIds,
              next.variants.reduce((_, v) => v.id, ''),
            ),
          );
          setToast(t('variants.kept', { name }));
        }}
      />
      {picked && (
        <MapPick
          place={picked}
          visits={picked.kind === 'city' ? visits(picked) : []}
          from={wayFrom}
          stops={plan.stops.length}
          onOpen={(stopId) => {
            setPicked(null);
            setSelectedId(stopId);
            show('stop', true);
          }}
          // A trip read only: a place looked at, no way drafted to it, no stop made of it.
          onRoute={
            locked
              ? undefined
              : () => {
                  // An airport by its code: TAY, not its name; a town by its name and where it was
                  // picked, Dover by the Channel, not Delaware's airport.
                  if (picked.code == null) {
                    const name = picked.name;
                    setPins((now) => [
                      ...now.filter((pin) => !pin.startsWith(`${name}@`)),
                      `${name}@${picked.lat.toFixed(4)},${picked.lon.toFixed(4)}`,
                    ]);
                  }
                  toRoute(picked.code ?? picked.name);
                }
          }
          onStop={
            locked
              ? undefined
              : () => {
                  asStop(picked);
                }
          }
          onClose={() => {
            setPicked(null);
          }}
        />
      )}
      {shownVariants.length > 0 && (
        <Proposal
          plan={plan}
          variants={shownVariants}
          onClose={() => {
            setVariantIds([]);
          }}
          onUsed={(next) => {
            setVariantIds([]);
            used(next);
          }}
          onFailed={() => {
            setFailed(true);
          }}
        />
      )}
      {undoable && (
        <p className="plan-toast plan-undo" role="status">
          {t('plan.variantUsed')}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              undoVariant(plan.trip.id).then(saved, () => {
                setFailed(true);
              });
            }}
          >
            {t('plan.undo')}
          </button>
        </p>
      )}
      {toast !== null && (
        <p className="plan-toast" role="status">
          {toast}
        </p>
      )}
      {failed && (
        <p className="form-failed plan-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}
      {shown.route && (
        <Widget
          id="route"
          title={t('plan.route')}
          place={LEFT}
          width={320}
          area={canvas}
          onClose={() => {
            show('route', false);
          }}
          actions={
            !locked && (
              <button
                type="button"
                className="icon-button icon-button-small icon-button-accent"
                aria-label={t('plan.byHand')}
                title={t('plan.byHand')}
                onClick={() => {
                  setEditing({ kind: 'new-stop' });
                }}
              >
                +
              </button>
            )
          }
        >
          <RouteList
            plan={plan}
            after={
              family && (
                <FamilyRoute
                  plan={plan}
                  family={family}
                  picked={kinPick?.way ? null : (kinPick?.stop ?? null)}
                  onPick={pickKin}
                />
              )
            }
            notes={notes}
            {...(family && family.trips.length > 1
              ? { kin: `kin-${String(kinOf(family, plan.trip.id).kin)}` }
              : {})}
            variants={kept}
            // A way picked is what is picked: no stop stays pressed beside it.
            selected={kinPick || wayStop ? null : place ? selectedId : (selected?.id ?? null)}
            picked={variantIds}
            onSelect={(key) => {
              choose(key);
              show('stop', true);
            }}
            onPick={pickVariant}
            onClear={() => {
              setVariantIds([]);
            }}
            onMove={(from, to) => {
              move(from, to - from);
            }}
            onAddAfter={(after) => {
              setEditing({ kind: 'new-stop', after });
            }}
            onPlaceDays={(variant, at, stop, days) => {
              setFailed(false);
              adjustVariant(
                plan.trip.id,
                variant.id,
                saidWith(variant, at, stop, { days }),
              ).then(setVariants, () => {
                setFailed(true);
              });
            }}
            onDays={(stop, days) => {
              setFailed(false);
              updateStop(plan.trip.id, stop.id, withDays(stop, days)).then(saved, () => {
                setFailed(true);
              });
            }}
          />
        </Widget>
      )}
      {shown.budget && (
        <Widget
          id="budget"
          title={t('plan.budget')}
          place={RIGHT}
          width={300}
          area={canvas}
          onClose={() => {
            show('budget', false);
          }}
        >
          <div className="plan-side">
            <BudgetCard plan={plan} />
            <StatsCard plan={plan} />
          </div>
        </Widget>
      )}
      {/* The trip's family: shown at first once the trip has branches, else from the menu. */}
      {family && families && (
        <Widget
          id="family"
          title={t('family.widget')}
          place={BOTTOM_RIGHT}
          width={300}
          area={canvas}
          onClose={() => {
            show('family', false);
          }}
        >
          <FamilyCard
            plan={plan}
            family={family}
            onFamily={setFamily}
            onBranch={() => {
              setBranching({ at: selectedId });
            }}
          />
        </Widget>
      )}
      {/* The days without a visa, each country's against its limit: a widget of its own. */}
      {shown.visa && docs && (
        <Widget
          id="visa"
          title={t('plan.visaTitle')}
          place={RIGHT}
          width={300}
          area={canvas}
          onClose={() => {
            show('visa', false);
          }}
        >
          <div className="plan-side">
            <VisaCard
              docs={docs}
              onPassport={(passport) => {
                setPassport(passport).then(() => getDocs(plan.trip.id).then(setDocs), quiet);
              }}
            />
          </div>
        </Widget>
      )}
      {shown.timeline && plan.stops.length > 0 && (
        <Widget
          id="timeline"
          title={t('timeline.title')}
          place={TOP}
          width={between}
          area={canvas}
          onClose={() => {
            show('timeline', false);
          }}
        >
          <Timeline
            // Another trip opened starts its timeline afresh: its own days in view, no clock pinned.
            key={plan.trip.id}
            plan={plan}
            moment={moment}
            family={family}
            weather={readyWeather(sky)}
            kinWeather={kinSky?.trip === kinId ? kinSky : null}
            onSky={(stop, day) => {
              setSkyDay({ stop, day });
              show('stop', true);
            }}
            onKin={pickKin}
            onKinDays={
              locked
                ? undefined
                : (trip, stop, days) => {
                    // Another trip's stop's days: changed on that trip, the family read again.
                    setFailed(false);
                    getPlan(trip)
                      .then((other) =>
                        Promise.all(
                          other.stops
                            .filter((one) => one.id === stop)
                            .map((at) => updateStop(trip, stop, withDays(at, days))),
                        ),
                      )
                      .then(() => getFamily(plan.trip.id))
                      .then(setFamily, () => {
                        setFailed(true);
                      });
                  }
            }
            // One stop picked at a time: another trip's, or this one's.
            selected={place || kinPick ? null : selectedId}
            kinPicked={kinPick?.way ? null : (kinPick?.stop ?? null)}
            kinWay={kinPick?.way ? kinPick.stop : null}
            way={way}
            onPick={(stop) => {
              choose(stop.id);
              setWay(null);
            }}
            onClear={() => {
              setKinPick(null);
              setSelectedId(null);
              setWay(null);
              setLoose(true);
            }}
            onOpen={(stop) => {
              choose(stop.id);
              show('stop', true);
            }}
            onDays={(stop, days) => {
              setFailed(false);
              updateStop(plan.trip.id, stop.id, withDays(stop, days)).then(saved, () => {
                setFailed(true);
              });
            }}
            onMove={(from, to) => {
              move(from, to - from);
            }}
            onShift={(stop, departureAt, arrivalAt) => {
              setFailed(false);
              updateStop(plan.trip.id, stop.id, withWayAt(stop, departureAt, arrivalAt)).then(
                saved,
                () => {
                  setFailed(true);
                },
              );
            }}
            onAddAfter={(after) => {
              setEditing({ kind: 'new-stop', after });
            }}
            onWay={pickWay}
          />
        </Widget>
      )}
      {/* What is picked, as an editor's properties: one widget that stays, whatever is picked
          or none, so the map is not made smaller and larger as things are picked. A way picked
          is said first, then a variant's place, then the stop. */}
      {shown.stop && (
        <Widget
          id="stop"
          // Another trip's stop in that trip's colour, the branch mark before its title.
          {...(kinTrip && kinPicked && family
            ? {
                tone: `widget-kin kin-${String(kinOf(family, kinTrip.id).kin)}`,
                icon: <BranchIcon />,
              }
            : {})}
          title={
            kinTrip && kinPicked && kinPick?.way
              ? t('way.widget', {
                  from: kinPicked.city,
                  to: kinTo(kinTrip, kinPicked),
                })
              : kinTrip && kinPicked
                ? t('family.stopTitle', {
                    city: kinPicked.city,
                    country: countryName(kinPicked.country, i18n.language),
                    trip: kinTrip.name,
                  })
                : wayStop
                  ? t('way.widget', {
                      from: wayStop.city,
                      to: plan.stops[plan.stops.indexOf(wayStop) + 1]?.city ?? '…',
                    })
                  : place
                    ? t('plan.placeTitle', {
                        city: place.stop.city,
                        country: countryName(place.stop.country, i18n.language),
                      })
                    : selected
                      ? t('plan.stopTitle', {
                          city: selected.city,
                          country: countryName(selected.country, i18n.language),
                        })
                      : t('plan.stopWidget')
          }
          place={BOTTOM}
          width={between}
          area={canvas}
          // Closed, it is no longer shown, and a way picked no longer either.
          onClose={() => {
            show('stop', false);
            setWay(null);
          }}
          actions={!kinPicked && wayStop && !locked ? wayActions(wayStop) : undefined}
        >
          {kinTrip && kinPicked && family ? (
            <KinStopDetail
              family={family}
              trip={kinTrip}
              stop={kinPicked}
              plan={kinPlan?.trip.id === kinTrip.id ? kinPlan : null}
              way={kinPick?.way === true}
              weatherDay={skyOf(kinPicked.id)}
              onPick={(stop, onWay) => {
                pickKin(kinTrip.id, stop, onWay);
              }}
            />
          ) : wayStop ? (
            <div className="way-widget">
              <PickedPath plan={plan} stop={wayStop} way onStop={pickStop} onWay={pickWay} />
              <WayDetail plan={plan} stop={wayStop} onPlan={locked ? undefined : saved} />
            </div>
          ) : place ? (
            <StopDetail
              plan={plan}
              stop={place.stop}
              variant={place.variant}
              {...draftsOf(place.variant, place.at)}
              onChange={
                locked
                  ? undefined
                  : (change) =>
                      adjustVariant(
                        plan.trip.id,
                        place.variant.id,
                        saidWith(place.variant, place.at, place.stop, change),
                      ).then(setVariants)
              }
            />
          ) : selected ? (
            <>
              <PickedPath
                plan={plan}
                stop={selected}
                way={false}
                onStop={pickStop}
                onWay={pickWay}
              />
              <StopDetail
                plan={plan}
                stop={selected}
                weatherDay={skyOf(selected.id)}
                daysAt={`/trips/${plan.trip.id}/stops/${selected.id}`}
                files={docs?.documents ?? []}
                // A trip read only: the stop said, nothing of it changed.
                {...(locked
                  ? {}
                  : {
                      onEdit: () => {
                        setEditing({ kind: 'stop', stop: selected });
                      },
                      onTicket: (way: TicketWay) => {
                        setTicketFor({ stop: selected, way });
                      },
                      onFuel: () => {
                        setFuelOpen(true);
                      },
                      onBook: () => {
                        setBookingFor(selected);
                      },
                      onBooking: (booking: Booking) => {
                        setChanging({ booking, stop: selected, sector: null });
                      },
                      onIncident: (booking: Booking) => {
                        setIncidentFor({ booking, stop: selected });
                      },
                      onBranch: () => {
                        setBranching({ at: selected.id });
                      },
                      // Not while a booked ticket goes to it.
                      onRemove:
                        heldBy(plan, selected) === null
                          ? () => {
                              setRemoving(selected);
                            }
                          : undefined,
                      onEnds: (ends: { from: Spot | null; to: Spot | null }) => {
                        setEnds(plan.trip.id, selected.id, ends).then(saved, () => {
                          setFailed(true);
                        });
                      },
                      onChange: (change: PlaceChange) =>
                        updateStop(
                          plan.trip.id,
                          selected.id,
                          stopInputOf(selected, change),
                        ).then(saved),
                    })}
              />
            </>
          ) : (
            <p className="quiet plan-nothing">{t('plan.nothingPicked')}</p>
          )}
        </Widget>
      )}
      {shown.more && (
        <Widget
          id="more"
          title={t('plan.moreWidget')}
          place={BOTTOM_LEFT}
          width={320}
          area={canvas}
          onClose={() => {
            show('more', false);
          }}
        >
          <div className="plan-side-wide">
            {docs && <DocsCard trip={plan.trip.id} docs={docs} />}
            <Travellers
              plan={plan}
              onSaved={(next) => {
                setState({ kind: 'ready', plan: next });
              }}
            />
          </div>
        </Widget>
      )}
      {shown.variants && variants && (
        <Widget
          id="variants"
          title={t('variants.widget')}
          place={BOTTOM}
          width={between}
          area={canvas}
          onClose={() => {
            show('variants', false);
          }}
        >
          <VariantsWidget
            plan={plan}
            variants={variants}
            picked={variantIds}
            onPick={pickVariant}
            onChanged={setVariants}
            onDraft={setQuery}
            onApplied={used}
            onFailed={() => {
              setFailed(true);
            }}
          />
        </Widget>
      )}
      <WidgetDock
        items={[
          { id: 'route', title: t('plan.route') },
          { id: 'budget', title: t('plan.budget') },
          { id: 'visa', title: t('plan.visaTitle') },
          { id: 'family', title: t('family.widget') },
          ...(plan.stops.length > 0 ? [{ id: 'timeline', title: t('timeline.title') }] : []),
          { id: 'stop', title: t('plan.stopWidget') },
          { id: 'variants', title: t('variants.widget') },
          { id: 'more', title: t('plan.moreWidget') },
        ]}
        shown={docked}
        onShow={show}
      />
      {ticketFor && (
        <BookingDialog
          plan={plan}
          draft={{
            stop: ticketFor.stop.id,
            kind: 'ticket',
            name: ticketFor.way.name,
            // The day of the flight it was planned on, else the day it leaves.
            day: ticketFor.stop.out?.flight?.day ?? ticketFor.stop.end,
            nights: 0,
            // In place of one cancelled: at its price until said.
            price: ticketFor.way.replaces?.price?.minor ?? null,
            currency: null,
            pay: 'paid',
            reference: null,
            link: ticketFor.way.link,
          }}
          way={{ ...ticketFor.way, towards: onFrom(ticketFor.stop) }}
          // Its times go on the way it is for, then the ticket is kept; the stops it goes on
          // through become changes on the way.
          save={(input, { departure, arrival, until }) =>
            updateStop(
              plan.trip.id,
              ticketFor.stop.id,
              stopInputOf(ticketFor.stop, {
                way: {
                  estimate: ticketFor.way.estimate,
                  ...ticketFor.way.kept({ departure, arrival }),
                  link: input.link,
                },
              }),
            )
              .then(() =>
                addBooking(plan.trip.id, {
                  ...input,
                  ...(ticketFor.way.replaces && { replaces: ticketFor.way.replaces.id }),
                }),
              )
              .then((next) =>
                through(ticketFor.stop, until).reduce(
                  (done, stop) => done.then(() => setTransfer(plan.trip.id, stop.id, true)),
                  Promise.resolve(next),
                ),
              )
          }
          onClose={() => {
            setTicketFor(null);
          }}
          onSaved={(next) => {
            setTicketFor(null);
            saved(next);
          }}
        />
      )}
      {bookingFor && (
        <BookingDialog
          plan={plan}
          draft={{
            stop: bookingFor.id,
            kind: 'stay',
            name: '',
            day: bookingFor.start,
            nights: bookingFor.days,
            price: null,
            currency: null,
            pay: 'paid',
            reference: null,
            link: null,
          }}
          save={(input) => addBooking(plan.trip.id, input)}
          onClose={() => {
            setBookingFor(null);
          }}
          onSaved={(next) => {
            setBookingFor(null);
            saved(next);
          }}
        />
      )}
      {branching && (
        <BranchDialog
          plan={plan}
          at={branching.at}
          onClose={() => {
            setBranching(null);
          }}
        />
      )}
      {incidentFor && (
        <IncidentDialog
          plan={plan}
          stop={incidentFor.stop}
          booking={incidentFor.booking}
          onClose={() => {
            setIncidentFor(null);
          }}
          onSaved={(next) => {
            setIncidentFor(null);
            saved(next);
          }}
        />
      )}
      {changing && (
        <ChangeBooking
          plan={plan}
          stop={changing.stop}
          booking={changing.booking}
          sector={changing.sector}
          onClose={() => {
            setChanging(null);
          }}
          onSaved={(next) => {
            setChanging(null);
            saved(next);
          }}
        />
      )}
      {fuelOpen && (
        <CarDialog
          plan={plan}
          onClose={() => {
            setFuelOpen(false);
          }}
          onSaved={(next) => {
            setFuelOpen(false);
            saved(next);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          open
          title={t('stopForm.removeQuestion', { city: removing.city })}
          body={t('stopForm.removeBody')}
          confirmLabel={t('stopForm.removeConfirm')}
          onCancel={() => {
            setRemoving(null);
          }}
          onConfirm={() => {
            const stop = removing;
            setRemoving(null);
            deleteStop(plan.trip.id, stop.id).then(
              (next) => {
                setSelectedId(null);
                saved(next);
              },
              () => {
                setFailed(true);
              },
            );
          }}
        />
      )}
      {editing.kind === 'new-stop' && (
        <StopDialog
          trip={plan.trip.id}
          currency={plan.trip.currency}
          {...(editing.start ? { start: editing.start } : {})}
          save={(input) =>
            addStop(plan.trip.id, input).then((next) => {
              // Asked for in days no stop covers: put in after the stop they follow.
              const after = editing.after;
              if (!after) return next;
              const made = next.stops.filter((s) => !plan.stops.some((was) => was.id === s.id));
              const order = plan.stops.flatMap((s) =>
                s.id === after.id ? [s.id, ...made.map((m) => m.id)] : [s.id],
              );
              return reorderStops(plan.trip.id, order);
            })
          }
          onClose={close}
          onSaved={saved}
        />
      )}
      {editing.kind === 'stop' && (
        <EditStop plan={plan} stop={editing.stop} onClose={close} onSaved={saved} />
      )}
    </DockFrame>
  );
}
